import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatMenuModule } from '@angular/material/menu'
import { BehaviorSubject, Observable } from 'rxjs'
import { take } from 'rxjs/operators'
import { FileObject } from '@tradecafe/types/core'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'
import { FilesService } from 'src/pages/admin/trading/deals/deal-documents/files.service'
import { ModalProxyService } from 'src/shared/modal'

interface CreditApplicationInput {
  file: FileObject
  file_id?: string
  type?: string
}

interface CreditApplication {
  file_id: string
  type: string
}

@Component({
  selector: 'tc-credit-applications-table',
  templateUrl: './credit-applications-table.component.html',
  styleUrl: './credit-applications-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
  ]
})

export class CreditApplicationsTableComponent {
  private readonly ConfirmModal = inject(ConfirmModalService)
  private readonly Files = inject(FilesService)
  private readonly modalHelper = inject(ModalProxyService)

  @Input({ required: true }) creditApplications$: Observable<CreditApplicationInput[]>
  @Input({ required: true }) files$: BehaviorSubject<Record<string, FileObject>>
  @Input({ required: true }) onChange: (applications: CreditApplication[]) => void
  @Input() type: 'document' | 'credit_application' = 'credit_application'
  @Input() canUpload?: () => boolean

  async upload() {
    if (this.canUpload && !this.canUpload()) {
      return
    }

    const modalTitle = this.type === 'credit_application' ? 'Add Credit Application' : 'Add Document'
    const {application, file} = await this.modalHelper.showModal({
      component: 'tcCompanyCreditApplicationForm',
      windowClass: 'modalShipping',
      backdrop: 'static',
      resolve: {
        title: () => modalTitle,
      },
    }) as { application: { file_id: string, type: string }, file: FileObject }

    this.files$.next({ ...this.files$.value, [file.file_id]: file })
    this.creditApplications$.pipe(take(1)).subscribe((current: CreditApplicationInput[]) => {
      const applications = current
        .filter((app): app is CreditApplicationInput & CreditApplication => app.file_id !== undefined && app.type !== undefined)
        .map(app => ({ file_id: app.file_id, type: app.type }))
      this.onChange([...applications, application])
    })
  }

  async delete(application: CreditApplicationInput) {
    const documentName = application.file.name
    const description = this.type === 'credit_application'
      ? `Are you sure you want to delete this credit application:<br><strong>${documentName}</strong>?`
      : `Are you sure you want to delete this document:<br><strong>${documentName}</strong>?`

    await this.ConfirmModal.show({
      title: 'Warning',
      description,
    })

    this.creditApplications$.pipe(take(1)).subscribe((current: CreditApplicationInput[]) => {
      const applications = current
        .filter(app => app.file_id !== application.file_id)
        .filter((app): app is CreditApplicationInput & CreditApplication => app.file_id !== undefined && app.type !== undefined)
        .map(app => ({ file_id: app.file_id, type: app.type }))
      this.onChange(applications)
    })
  }

  download(application: CreditApplicationInput) {
    this.Files.download(application.file)
  }
}
